





















































import { defineComponent, ref, computed, onMounted } from '@vue/composition-api';
import { ButtonType, ModalSize, IconSize } from '@getaccept/lib-ui';
import GetAcceptLogo from '@getaccept/lib-ui/src/assets/img/getaccept-logo-blue-text.svg';
import { EnumDocumentType } from '@getaccept/lib-shared/src/enums/DocumentType';
import {
  AnalyticsEvent,
  AnalyticsOnboardingChoice,
  AnalyticsUserTrait,
} from '@getaccept/lib-shared/src/types/analytics';
import Analytics from '@getaccept/lib-shared/src/analytics';
import confetti from 'canvas-confetti';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import { useToast } from '@getaccept/lib-shared/src/toast';
import { PathName } from '@getaccept/lib-shared/src/enums/PathNames';
import { Feature, useFeatureStore } from '@getaccept/feature-restriction';
import { storeToRefs } from 'pinia';
import TemplateThumbnail from '../../assets/onboarding/template-thumbnail.svg';
import DocumentThumbnail from '../../assets/onboarding/document-thumbnail.svg';
import { DocumentService } from '../../api/documents/services/document.service';
import router from '../../router/router';

export default defineComponent({
  components: {
    DocumentThumbnail,
    TemplateThumbnail,
    GetAcceptLogo,
  },
  props: { userHash: { type: String, default: '' } },
  setup(props, { emit }) {
    const { toast } = useToast();
    const creating = ref(false);
    const redirectedFromSetupWizard = ref(false);

    const { features } = storeToRefs(useFeatureStore());

    const onboardingWelcomeText = computed(() =>
      redirectedFromSetupWizard.value ? t('onboarding-welcome-setup') : t('onboarding-welcome')
    );

    const onboardingSubheaderText = computed(() =>
      redirectedFromSetupWizard.value ? t('onboarding-subheader-setup') : t('onboarding-subheader')
    );

    const handleDocumentOnboarding = async () => {
      handleAnalyticsOnboarding(AnalyticsOnboardingChoice.Upload);
      await createDocument();
    };

    const createDocument = async () => {
      creating.value = true;
      try {
        const id: string = await DocumentService.createDocument(EnumDocumentType.Document);
        emit('documentCreated', { id, type: EnumDocumentType.Document });
        emit('close');
      } catch (e) {
        toast.danger(t('could-not-create-document'));
      } finally {
        creating.value = false;
      }
    };

    const handleGoTemplateGallery = () => {
      handleAnalyticsOnboarding(AnalyticsOnboardingChoice.Template);
      emit('close');
      router.push({
        path: '/content-library',
        query: { type: 'intro' },
      });
    };

    const handleDismiss = () => {
      handleAnalyticsOnboarding(AnalyticsOnboardingChoice.Dismiss);
      emit('close');
      router.replace(PathName.Dashboard);
    };

    const handleAnalyticsOnboarding = (choice: AnalyticsOnboardingChoice) => {
      Analytics.identify(props.userHash, {
        [AnalyticsUserTrait.OnboardingStep]: 1,
        [AnalyticsUserTrait.OnboardingChoice]: choice,
      });
      Analytics.track(AnalyticsEvent.OnboardingStep1, {
        [AnalyticsUserTrait.OnboardingStep]: 1,
        [AnalyticsUserTrait.OnboardingChoice]: choice,
      });
    };

    const fire = (
      particleRatio: number,
      opts: { spread: number; startVelocity?: number; decay?: number; scalar?: number }
    ) => {
      confetti(
        Object.assign({}, { origin: { y: 0.7 } }, opts, {
          zIndex: 2147483003,
          particleCount: Math.floor(300 * particleRatio),
        })
      );
    };

    const showConfetti = () => {
      fire(0.25, {
        spread: 26,
        startVelocity: 55,
      });
      fire(0.2, {
        spread: 60,
      });
      fire(0.35, {
        spread: 100,
        decay: 0.91,
        scalar: 0.8,
      });
      fire(0.1, {
        spread: 120,
        startVelocity: 25,
        decay: 0.92,
        scalar: 1.2,
      });
      fire(0.1, {
        spread: 120,
        startVelocity: 45,
      });
    };

    const handleAnalyticsTracking = () => {
      Analytics.identify(props.userHash, {
        [AnalyticsUserTrait.OnboardingStep]: 0,
      });
      Analytics.track(AnalyticsEvent.OnboardingLoaded, {
        [AnalyticsUserTrait.OnboardingStep]: 0,
      });
    };

    onMounted(() => {
      showConfetti();
      handleAnalyticsTracking();
      redirectedFromSetupWizard.value = router.currentRoute.query.intro === 'setup';
    });

    return {
      ButtonType,
      ModalSize,
      creating,
      redirectedFromSetupWizard,
      IconSize,
      onboardingWelcomeText,
      onboardingSubheaderText,
      handleDocumentOnboarding,
      createDocument,
      handleGoTemplateGallery,
      handleDismiss,
      handleAnalyticsOnboarding,
      fire,
      showConfetti,
      handleAnalyticsTracking,
      features,
      Feature,
    };
  },
});
